import { NormalLink } from '@elements/Button';
import { AccentText, BoldText, MuteText } from '@elements/Common';
import { CommonUtility } from '@utility/common';
import {
  CommonConstant,
  ItemPriceRule,
  ItemStates,
  Tabs,
} from '@utility/constants/common';
import { Link, useNavigate } from 'react-router';
import { CurrencyDollar, Info } from '@phosphor-icons/react';
import { Badge, type BadgeTone, cn, Image, Tooltip } from '@skupreme/shared/ui';
import TextCopyToClipboard from '@components/TextCopyToClipboard';
import {
  cloneElement,
  isValidElement,
  type HTMLAttributes,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useMemo,
} from 'react';
import { useBundleByItemHook } from '@hooks/item';
import { MarketplaceIcon } from './ConnectedMarketplaces';
import {
  type ISemanticImageWithFallbackProps,
  type SemanticImageSize,
  SemanticImageWithFallback,
} from './FallbackImage';
import { Flag } from '@elements/Flag';
import type { ICatalogProduct } from 'types/common/catalog';

/** A square, uncropped product picture at a Semantic image size. */
export function CustomImage({
  className,
  ...rest
}: ISemanticImageWithFallbackProps) {
  return (
    <SemanticImageWithFallback
      {...rest}
      className={cn(
        'sk:static sk:aspect-square sk:rounded-option sk:object-contain',
        className,
      )}
    />
  );
}

/** Bold identifier text with its label and copy icon on one wrapping line. */
export function UPCText({
  className,
  ...rest
}: HTMLAttributes<HTMLSpanElement>) {
  return (
    <BoldText
      {...rest}
      className={cn(
        'sk:flex sk:min-w-[163px] sk:flex-wrap sk:items-center sk:gap-x-1',
        className,
      )}
    />
  );
}

export function ActiveLink({
  className,
  ...rest
}: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      {...rest}
      className={cn(
        'sk:cursor-pointer sk:text-sm sk:leading-4 sk:font-bold',
        className,
      )}
    />
  );
}

type CatalogStatus =
  | 'Missing Information'
  | 'Duplicate Listing'
  | 'Conflict'
  | 'Discontinued'
  | 'Inactive'
  | 'Component'
  | 'Kit';

/* What needs fixing reads as danger; a state the item is merely in does not. */
const STATUS_TONES: Record<CatalogStatus, BadgeTone> = {
  'Missing Information': 'danger',
  'Duplicate Listing': 'danger',
  Conflict: 'danger',
  Discontinued: 'neutral',
  Inactive: 'neutral',
  Component: 'info',
  Kit: 'accent',
};

/*
 * A badge that leads to the conflict screen. It sits inside the title's link,
 * so it is a span acting as a link rather than a nested anchor or button.
 */
function BadgeAction({
  onPress,
  children,
}: {
  onPress: (e?: MouseEvent) => void;
  children: ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: an <a> here would nest inside the title's link
    <span
      role="link"
      tabIndex={0}
      className="sk:cursor-pointer"
      onClick={onPress}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          onPress();
        }
      }}
    >
      {children}
    </span>
  );
}

function StatusBadge({ status }: { status: CatalogStatus }) {
  return (
    <Badge
      tone={STATUS_TONES[status]}
      className="sk:mr-1 sk:px-2 sk:py-0 sk:align-middle sk:text-xs"
    >
      {status}
    </Badge>
  );
}

/** An identifier line under a title: muted label, then the value with a copy icon. */
function IdentifierRow({
  label,
  value,
  truncate = false,
}: {
  label: string;
  value: string;
  truncate?: boolean;
}) {
  return (
    <div className="sk:flex sk:min-w-0">
      <MuteText className="sk:mr-2 sk:whitespace-nowrap">{label}</MuteText>
      <BoldText
        className={cn(
          'sk:min-w-0 sk:font-normal sk:text-fg',
          truncate && 'sk:w-full sk:overflow-hidden sk:text-ellipsis',
        )}
      >
        <TextCopyToClipboard text={value} iconSize={16} />
      </BoldText>
    </div>
  );
}

function StorageClass({ storageClass }: { storageClass: string }) {
  return (
    <div className="sk:w-full">
      <Tooltip content="Storage Class" placement="bottom start">
        <MuteText>{storageClass}</MuteText>
      </Tooltip>
    </div>
  );
}

interface IItemImageProps {
  disabled?: boolean;
  item?: ICatalogProduct;
  /** Semantic sizes: `mini` is the kit's 35px thumbnail, anything larger its 60px one. */
  size?: SemanticImageSize;
  imageKey?: string;
  extraClasses?: string;
  noMargin?: boolean;
  className?: string;
  /** Shown instead of the item's own image. */
  src?: string;
}

const getImage = (
  product: ICatalogProduct | undefined,
  imageKey: string,
): string => {
  if (
    product &&
    Object.hasOwn(product, imageKey) &&
    product[imageKey]?.display_image
  ) {
    return product[imageKey].display_image;
  }
  return CommonConstant.defaultImage;
};

/** A catalog item's thumbnail on the kit Image: a fixed box whatever the URL does. */
export function ItemImage({
  disabled,
  item,
  size,
  imageKey = 'p_images',
  extraClasses,
  noMargin = false,
  className,
  src: srcOverride,
}: IItemImageProps): ReactNode {
  const src = srcOverride ?? getImage(item, imageKey);
  const hasImage = !!src && src !== CommonConstant.defaultImage;

  return (
    <Image
      src={hasImage ? src : null}
      alt=""
      size={size === 'mini' ? 'md' : 'lg'}
      fit="contain"
      className={cn(
        !noMargin && 'sk:mx-auto sk:flex',
        !hasImage && 'no-image',
        disabled && 'sk:opacity-45',
        extraClasses,
        className,
      )}
    />
  );
}

interface IItemConflictTitleProps {
  asin?: string;
  item: ICatalogProduct;
  upc?: string;
  title: string;
  sku?: string;
  id: string | number;
  storageClass?: string;
  disabled?: boolean;
  isDiscontinued?: boolean;
  isMissingInfo?: boolean;
  isDuplicateListing?: boolean;
  tagRenderer?: ReactElement | null;
  isInactive?: boolean;
}

export function ItemConflictTitle({
  asin,
  item,
  upc,
  title,
  sku,
  id,
  storageClass,
  disabled,
  isDiscontinued = false,
  isMissingInfo = false,
  isDuplicateListing,
  tagRenderer = null,
  isInactive = false,
}: IItemConflictTitleProps): ReactNode {
  const navigate = useNavigate();

  const redirectToMissingInfo = (e?: MouseEvent): void => {
    e?.stopPropagation();
    e?.preventDefault();

    const tab = item.is_duplicate_listing
      ? Tabs.catalogConflict.mergeListing
      : Tabs.catalogConflict.resolveConflict;
    navigate(
      `/app/catalogs/${tab}/?sku=${encodeURIComponent(item.p_sku as string)}`,
    );
  };

  return (
    <div className="sk:flex sk:flex-col sk:items-center">
      <Link to={`/app/catalogs/catalog/${encodeURIComponent(id)}`}>
        <NormalLink className="sk:w-full" disabled={disabled}>
          <div>
            <span>
              {title}&nbsp;
              {isMissingInfo && (
                <BadgeAction onPress={redirectToMissingInfo}>
                  <StatusBadge
                    status={
                      isDuplicateListing
                        ? 'Duplicate Listing'
                        : 'Missing Information'
                    }
                  />
                </BadgeAction>
              )}
              {item.is_bundle && <StatusBadge status="Kit" />}
            </span>
            {isValidElement(tagRenderer) && cloneElement(tagRenderer)}
            {isDiscontinued && (
              <StatusBadge status={isInactive ? 'Inactive' : 'Discontinued'} />
            )}
          </div>
          {sku && <IdentifierRow label="SKU" value={sku} truncate />}
          {asin && <IdentifierRow label="ASIN" value={asin} truncate />}
          {upc && <IdentifierRow label="UPC" value={upc} truncate />}
        </NormalLink>
      </Link>
      {storageClass && <StorageClass storageClass={storageClass} />}
    </div>
  );
}

interface IItemTitleProps {
  asin?: string;
  upc?: string;
  title: string;
  sku?: string;
  marketplace_sku?: string;
  id?: string | number;
  isBundled?: boolean;
  storageClass?: string;
  wrapperClass?: string;
  disabled?: boolean;
  isSummaryView?: boolean;
  isDiscontinued?: boolean;
  isMissingInfo?: boolean;
  tabIndex?: number;
  isDuplicateListing?: boolean;
  tagRenderer?: ReactElement | null;
  isInConflict?: boolean;
  isInactive?: boolean;
  state?: string;
}

export function ItemTitle({
  asin,
  upc,
  title,
  sku,
  marketplace_sku,
  id,
  isBundled,
  storageClass,
  wrapperClass = 'sk:items-start!',
  disabled,
  isSummaryView = false,
  isDiscontinued = false,
  isMissingInfo = false,
  tabIndex,
  isDuplicateListing = false,
  tagRenderer = null,
  isInConflict = false,
  isInactive,
  state,
}: IItemTitleProps): ReactNode {
  const navigate = useNavigate();

  const redirectToMissingInfo = (e?: MouseEvent): void => {
    e?.stopPropagation();
    e?.preventDefault();

    const url = `/app/catalogs/${
      isDuplicateListing
        ? Tabs.catalogConflict.mergeListing
        : Tabs.catalogConflict.resolveConflict
    }/?sku=${encodeURIComponent(sku ?? '')}`;

    navigate(url);
  };

  const linkContent = (
    <>
      <div className="sk:flex sk:items-center">
        <span className="sk:max-md:line-clamp-2">
          {title}&nbsp;
          {isDiscontinued && (
            <StatusBadge
              status={
                isInactive
                  ? 'Inactive'
                  : state === ItemStates.component
                    ? 'Component'
                    : 'Discontinued'
              }
            />
          )}
          {isMissingInfo && (
            <BadgeAction onPress={redirectToMissingInfo}>
              <StatusBadge
                status={
                  isDuplicateListing
                    ? 'Duplicate Listing'
                    : 'Missing Information'
                }
              />
            </BadgeAction>
          )}
          {isInConflict && (
            <BadgeAction onPress={redirectToMissingInfo}>
              <StatusBadge status="Conflict" />
            </BadgeAction>
          )}
          {isValidElement(tagRenderer) && cloneElement(tagRenderer)}
          {isBundled && <StatusBadge status="Kit" />}
        </span>
      </div>
      {sku && <IdentifierRow label="SKU" value={sku} />}
      {marketplace_sku && (
        <IdentifierRow label="Marketplace SKU" value={marketplace_sku} />
      )}
      {asin && <IdentifierRow label="ASIN" value={asin} />}
      {upc && <IdentifierRow label="UPC" value={upc} />}
    </>
  );

  return (
    <div className={cn('sk:flex sk:flex-col sk:items-center', wrapperClass)}>
      {isSummaryView ? (
        <div className="link-summary-view">{linkContent}</div>
      ) : id ? (
        <Link to={`/app/catalogs/catalog/${encodeURIComponent(id)}`}>
          <NormalLink
            className="sk:w-full"
            disabled={disabled}
            tabIndex={tabIndex}
          >
            {linkContent}
          </NormalLink>
        </Link>
      ) : (
        <div className="sk:w-full">{linkContent}</div>
      )}
      {storageClass && <StorageClass storageClass={storageClass} />}
    </div>
  );
}

interface IMarketplacePrice {
  marketplace_id: string | number;
  key: string;
  marketplace_alias: string;
  price: number;
}

interface IPriceBoxProps {
  prices: IMarketplacePrice[];
  basePrice: number;
  currency: string;
}

const POPUP_HEADING = 'sk:mb-2 sk:text-sm sk:font-semibold sk:text-fg';
const POPUP_ROW =
  'sk:flex sk:items-center sk:justify-between sk:gap-4 sk:border-b sk:border-rule sk:py-2 sk:last:border-b-0';

function PriceBox({ prices, basePrice, currency }: IPriceBoxProps): ReactNode {
  return (
    <div className="sk:max-h-[350px] sk:w-[min(450px,90vw)] sk:overflow-y-auto">
      <div className={POPUP_HEADING}>Manually Set</div>
      <div className={POPUP_ROW}>
        <span className="sk:flex sk:items-center sk:gap-1.5">
          <CurrencyDollar size={16} aria-hidden />
          Base Price:
        </span>
        <BoldText>{CommonUtility.currencyFormat(basePrice, currency)}</BoldText>
      </div>
      {prices.map(item => (
        <div key={item.marketplace_id} className={POPUP_ROW}>
          <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-1.5">
            <MarketplaceIcon
              marketplace={item.key}
              extraClasses="sk:m-0!"
              error={undefined}
              onNoImageFunction={undefined}
              shopName={undefined}
            />
            <span className="sk:truncate">{item.marketplace_alias}:</span>
          </span>
          <span className="sk:flex sk:items-center sk:gap-1 sk:whitespace-nowrap">
            <BoldText>
              {CommonUtility.currencyFormat(item.price, currency)}
            </BoldText>
            <MuteText>{`(Base Price ${
              basePrice > item.price ? '' : '+'
            }${CommonUtility.currencyFormat(item.price - basePrice)})`}</MuteText>
          </span>
        </div>
      ))}
    </div>
  );
}

interface IItemPriceProps {
  priceRule: string;
  basePrice: number;
  currency: string;
  marketplacePrices: IMarketplacePrice[];
}

export function ItemPrice({
  priceRule,
  basePrice,
  currency,
  marketplacePrices,
}: IItemPriceProps): ReactNode {
  if (priceRule !== ItemPriceRule.manual) {
    return <span>{CommonUtility.currencyFormat(basePrice, currency)}</span>;
  }

  return (
    <Tooltip
      placement="bottom"
      className="sk:max-w-none"
      content={
        <PriceBox
          prices={marketplacePrices}
          basePrice={basePrice}
          currency={currency}
        />
      }
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents: it only keeps the row's own click from firing; the tooltip is the interaction */}
      <span
        className="sk:inline-flex sk:cursor-pointer sk:items-center sk:gap-1"
        onClick={e => e.stopPropagation()}
      >
        <span className="sk:underline">Multiple</span>
        <Info aria-hidden />
      </span>
    </Tooltip>
  );
}

interface IInventoryItem {
  fulfillment_provider_id: string | number;
  fulfillment_provider_name: string;
  available: number;
  is_warehouse?: boolean;
  is_fulfillable?: boolean;
  isVirtual?: boolean;
}

interface IQuantityDetailsListProps {
  title?: string;
  list?: IInventoryItem[];
  total?: number;
  virtualList?: IInventoryItem[];
  virtualInventory?: number;
}

interface IBundleData {
  inventoryDetails?: IInventoryItem[];
}

interface IQuantityDetailsProps {
  total: number;
  list?: IInventoryItem[];
  bottomPosition?: boolean;
  justifyContentLeft?: boolean;
  item?: {
    is_bundle?: boolean;
    _id?: string;
  };
}

function InventoryTable({
  title,
  kind,
  list,
  total,
}: {
  title: string;
  kind: string;
  list: IInventoryItem[];
  total: number;
}) {
  return (
    <div className="sk:rounded-option sk:bg-fill-quaternary sk:px-3 sk:pt-2">
      <div className={POPUP_HEADING}>
        {title} <MuteText>({kind})</MuteText>
      </div>
      <div className="sk:flex sk:justify-between sk:border-b sk:border-rule sk:pb-1 sk:text-fg-secondary">
        <span>Location</span>
        <span>Units</span>
      </div>
      {list.map(row => (
        <div key={row.fulfillment_provider_id} className={POPUP_ROW}>
          <span>{row.fulfillment_provider_name}</span>
          <strong>{CommonUtility.numberWithCommas(row.available)}</strong>
        </div>
      ))}
      <div className={cn(POPUP_ROW, 'sk:border-t sk:font-semibold')}>
        <span>Total</span>
        <strong>{CommonUtility.numberWithCommas(total)}</strong>
      </div>
    </div>
  );
}

function QuantityDetailsList({
  title = '',
  list = [],
  total = 0,
  virtualList = [],
  virtualInventory = 0,
}: IQuantityDetailsListProps): ReactNode | null {
  if (
    !(
      CommonUtility.isValidArray(list) ||
      CommonUtility.isValidArray(virtualList)
    )
  ) {
    return null;
  }

  return (
    <div className="sk:min-w-[250px] sk:shrink-0">
      <div className={POPUP_HEADING}>{title}</div>
      <div className="sk:flex sk:flex-col sk:gap-2.5">
        {list.length > 0 && (
          <InventoryTable
            title="Assembled Inventory"
            kind="Physical"
            list={list}
            total={total}
          />
        )}
        {virtualList.length > 0 && (
          <InventoryTable
            title="Component Inventory"
            kind="Virtual"
            list={virtualList}
            total={virtualInventory}
          />
        )}
      </div>
    </div>
  );
}

export function QuantityDetails({
  total,
  list,
  bottomPosition,
  justifyContentLeft,
  item,
}: IQuantityDetailsProps): ReactNode {
  const { data } = useBundleByItemHook(item?.is_bundle, item?._id);
  const bundleData = data as IBundleData | null;

  const {
    listInStorage,
    listReadyForFulfillment,
    totalInStorage,
    totalReadyForFulfillment,
    virtualInventory,
    listVirtualInventory,
  } = useMemo(() => {
    const inStorage: IInventoryItem[] = [];
    let storageTotal = 0;
    const readyForFulfillment: IInventoryItem[] = [];
    let readyTotal = 0;
    const virtualRows: IInventoryItem[] = [];
    let virtualTotal = 0;

    (list ?? []).forEach(d => {
      if (d?.is_warehouse === true) {
        inStorage.push(d);
        if (d?.available > 0) storageTotal += d.available;
      } else if (d?.is_fulfillable === true) {
        readyForFulfillment.push(d);
        if (d?.available > 0) readyTotal += d.available;
      }
    });

    bundleData?.inventoryDetails?.forEach(inventory => {
      if (inventory?.available > 0) {
        virtualTotal += inventory.available;
        virtualRows.push({ ...inventory, isVirtual: true });
      }
    });

    return {
      listInStorage: inStorage,
      listReadyForFulfillment: readyForFulfillment,
      totalInStorage: storageTotal,
      totalReadyForFulfillment: readyTotal,
      virtualInventory: virtualTotal,
      listVirtualInventory: virtualRows,
    };
  }, [list, bundleData]);

  const hasDetails =
    CommonUtility.isValidArray(listReadyForFulfillment) ||
    CommonUtility.isValidArray(listVirtualInventory) ||
    CommonUtility.isValidArray(listInStorage);

  return (
    <Tooltip
      placement={bottomPosition ? 'bottom' : 'top'}
      className="sk:max-w-none"
      triggerClassName={cn(
        'sk:w-fit',
        !justifyContentLeft && 'sk:mx-auto sk:flex',
      )}
      content={
        <div className="sk:flex sk:gap-6">
          <QuantityDetailsList
            title="Ready for Fulfillment"
            list={listReadyForFulfillment}
            total={totalReadyForFulfillment}
            virtualList={listVirtualInventory}
            virtualInventory={virtualInventory}
          />
          <QuantityDetailsList
            title="In Storage"
            list={listInStorage}
            total={totalInStorage}
          />
          {!hasDetails && <div className={POPUP_HEADING}>No details found</div>}
        </div>
      }
    >
      <span
        className={cn(
          'sk:flex sk:w-fit sk:cursor-pointer sk:items-center sk:gap-1',
          !justifyContentLeft && 'sk:justify-center',
        )}
      >
        {(!!total || !virtualInventory) && (
          <AccentText className="sk:whitespace-nowrap">
            {CommonUtility.numberWithCommas(total)}
          </AccentText>
        )}
        {!!virtualInventory && (
          <MuteText className="sk:whitespace-nowrap">
            ({virtualInventory} Virtual)
          </MuteText>
        )}
        <Info size={16} className="sk:text-fg-tertiary" aria-hidden />
      </span>
    </Tooltip>
  );
}

interface IRegionalItem {
  item_id: string;
  region_alias: string;
  name: string;
}

function RegionBox({ items }: { items?: IRegionalItem[] }): ReactNode {
  return (
    <div className="sk:max-h-[350px] sk:min-w-[170px] sk:overflow-y-auto">
      <div className={POPUP_HEADING}>Region Items</div>
      {(items || []).map(item => (
        <div
          key={item.item_id}
          className={cn(POPUP_ROW, 'sk:justify-start sk:gap-1.5')}
        >
          <Flag title={item.region_alias} name={item.name.toLowerCase()} />
          <span className="sk:truncate">{item.region_alias}</span>
        </div>
      ))}
    </div>
  );
}

interface IItemSkuRegionBoxProps {
  item: {
    p_sku: string;
    regional_items?: IRegionalItem[];
  };
  link?: string;
}

export function ItemSkuRegionBox({
  item,
  link,
}: IItemSkuRegionBoxProps): ReactNode {
  const sku = (
    <ActiveLink>
      <TextCopyToClipboard
        className="sk:mt-1!"
        text={item.p_sku}
        iconSize={14}
      />
    </ActiveLink>
  );

  return (
    <Tooltip
      placement="bottom start"
      content={<RegionBox items={item.regional_items} />}
    >
      {link ? <Link to={link}>{sku}</Link> : sku}
    </Tooltip>
  );
}

const ROW_OF_IMAGES =
  'sk:mb-4 sk:flex sk:min-h-20 sk:flex-row sk:items-center sk:justify-center sk:gap-x-1.5 sk:text-left';

function TotalItems({ count }: { count: number }) {
  return (
    <div className="sk:absolute sk:bottom-2 sk:flex sk:w-[90%]">
      <MuteText>{count} total items</MuteText>
    </div>
  );
}

interface IProductRowProps {
  products: ICatalogProduct[];
  containerClass?: string;
  showDetail?: boolean;
}

export function ProductRow({
  products,
  containerClass = '',
  showDetail = true,
}: IProductRowProps): ReactNode {
  return (
    <>
      <div className={cn(ROW_OF_IMAGES, containerClass)}>
        {products.slice(0, 3).map(item => (
          <div className="img-container" key={item?._id || item?.price || ''}>
            <ItemImage item={item} />
          </div>
        ))}
      </div>
      {showDetail && products?.length > 3 && (
        <TotalItems count={products.length} />
      )}
    </>
  );
}

interface IItemComponentsRowProps {
  images: string[];
  containerClass?: string;
  showDetail?: boolean;
}

export function ItemComponentsRow({
  images,
  containerClass = '',
  showDetail = true,
}: IItemComponentsRowProps): ReactNode {
  return (
    <>
      <div className={cn(ROW_OF_IMAGES, containerClass)}>
        {images.slice(0, 3).map(image => (
          <div className="sk:mx-2" key={image}>
            <ItemImage item={undefined} src={image} noMargin />
          </div>
        ))}
      </div>
      {showDetail && images?.length > 3 && <TotalItems count={images.length} />}
    </>
  );
}
